import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2, Copy, Check, GripVertical } from "lucide-react";
import { formatLogText } from "../../../utils/formatLogText";
import { ParticipantBadge } from "../../common/components/ParticipantBadge";
import { Badge } from "@/components/ui/badge";
import { StarRating } from "@/components/ui/star-rating";
import { isColorLight } from "../../../utils/participantColors";
import { LogEntry } from "../types";

const REORDER_MIME = "application/x-braindog-log-reorder";

const stripTimecodeFrames = (tc?: string): string => {
  if (!tc) return tc ?? "";
  const parts = tc.split(":");
  return parts.length >= 4 ? parts.slice(0, 3).join(":") : tc;
};

const TranscriptDropIndicator: React.FC<{
  index: number;
  label: string;
  timecodes: string[];
}> = ({ index, label, timecodes }) => {
  const timecodeLabel =
    timecodes.length === 1 ? stripTimecodeFrames(timecodes[0]) : `${timecodes.length} TC`;

  return (
    <span
      data-testid={`transcript-drop-indicator-${index}`}
      role="status"
      aria-label={`${label}: ${timecodeLabel}`}
      style={{
        position: "absolute",
        width: "1px",
        height: "1px",
        padding: 0,
        margin: "-1px",
        overflow: "hidden",
        clip: "rect(0, 0, 0, 0)",
        whiteSpace: "nowrap",
        border: 0,
      }}
    >
      {label} · {timecodeLabel}
    </span>
  );
};

export interface RecentLogsPanelProps {
  logs: LogEntry[];
  participants: any[];
  directors?: any[];
  editingLogId?: string | null;
  onEditLog?: (log: LogEntry) => void;
  onUpdateLog?: (log: LogEntry) => void;
  onDeleteLog?: (logId: string) => void;
  onDrop?: (
    text: string,
    isOtf: boolean,
    sources?: number[],
    startAts?: number[],
    endAts?: number[],
    words?: any[][],
    channels?: number[],
    insertAt?: number,
  ) => void;
  onReorderLogs?: (orderedIds: string[]) => void;
  getCameraName?: (id: string) => string;
  getParticipantName?: (id: string) => string;
  getTagColor?: (id: string) => string;
  getTagLabel?: (id: string) => string;
  newestAt?: "top" | "bottom";
  showHeader?: boolean;
  draggedTranscriptionTimecodes?: string[] | null;
}

const RecentLogsPanelComponent: React.FC<RecentLogsPanelProps> = ({
  logs,
  participants,
  directors = [],
  editingLogId,
  onEditLog,
  onUpdateLog,
  onDeleteLog,
  onDrop,
  onReorderLogs,
  getCameraName = () => "",
  getParticipantName = (id) => id,
  getTagColor = () => "#666",
  getTagLabel = (id) => id,
  draggedTranscriptionTimecodes = null,
}) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevLogCountRef = useRef(logs.length);
  const prevLogIdsRef = useRef(new Set(logs.map((log) => log.id)));
  const logEntryRefs = useRef(new Map<string, HTMLDivElement>());
  const [copied, setCopied] = useState(false);
  const [draggingLogId, setDraggingLogId] = useState<string | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [isExternalDragOver, setIsExternalDragOver] = useState(false);
  const [externalDropIndex, setExternalDropIndex] = useState<number | null>(null);
  const [expandedMetadataLogIds, setExpandedMetadataLogIds] = useState<Set<string>>(
    () => new Set(),
  );

  const toggleMetadata = React.useCallback((logId: string) => {
    setExpandedMetadataLogIds((previous) => {
      const next = new Set(previous);
      if (next.has(logId)) next.delete(logId);
      else next.add(logId);
      return next;
    });
  }, []);

  const clearReorderState = () => {
    setDraggingLogId(null);
    setDropIndex(null);
  };

  const getExternalDropIndex = React.useCallback(
    (clientY: number) => {
      for (const [index, log] of logs.entries()) {
        const element = logEntryRefs.current.get(log.id);
        if (!element) continue;
        const rect = element.getBoundingClientRect();
        if (clientY < rect.top + rect.height / 2) return index;
      }
      return logs.length;
    },
    [logs],
  );

  const clearExternalDropState = () => {
    setIsExternalDragOver(false);
    setExternalDropIndex(null);
  };

  const handleReorderDrop = (draggedId: string, insertAt: number) => {
    if (!onReorderLogs) return;
    const fromIndex = logs.findIndex((log) => log.id === draggedId);
    if (fromIndex === -1) return;

    const orderedIds = logs.map((log) => log.id);
    orderedIds.splice(fromIndex, 1);
    const target = insertAt > fromIndex ? insertAt - 1 : insertAt;
    orderedIds.splice(Math.max(0, Math.min(target, orderedIds.length)), 0, draggedId);

    if (orderedIds.some((id, index) => id !== logs[index].id)) {
      onReorderLogs(orderedIds);
    }
  };

  const handleCopyAll = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (logs.length === 0) return;

    const lines = logs.map((log) => {
      const type =
        (log as any).type === "otf"
          ? "[OTF]"
          : (log as any).type === "transcription"
            ? "[TRANS]"
            : "[LOG]";

      const camNames = (log.cameras || [])
        .map((c) => getCameraName(c))
        .filter(Boolean)
        .join(", ");
      const partNames = (log.participants || []).map((p) => getParticipantName(p)).join(", ");
      const tagNames = (log.tags || []).map((tg) => getTagLabel(tg)).join(", ");

      const meta: string[] = [];
      if (camNames) meta.push(`cams: ${camNames}`);
      if (partNames) meta.push(`participants: ${partNames}`);
      if (tagNames) meta.push(`tags: ${tagNames}`);
      if (log.rating) meta.push(`★${log.rating}`);

      const metaStr = meta.length ? ` (${meta.join(" | ")})` : "";
      return `[${log.tc}] ${type} ${log.text}${metaStr}`;
    });

    const payload = lines.join("\n");

    try {
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore — clipboard might be blocked
    }
  };

  // Auto-scroll only when NEW logs arrive (length grows). Recent Logs is
  // ordered by timecode/manual order, so a new transcription can be inserted
  // anywhere in the list; scrolling to the end hides the item the user just
  // launched. Focus the actual inserted element instead. In-place edits
  // (star rating, etc.) and reorders must not yank the scroll position.
  useEffect(() => {
    if (logs.length > prevLogCountRef.current) {
      const addedLogs = logs.filter((log) => !prevLogIdsRef.current.has(log.id));
      const insertedLog = addedLogs.at(-1);
      const insertedElement = insertedLog ? logEntryRefs.current.get(insertedLog.id) : null;

      insertedElement?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    prevLogCountRef.current = logs.length;
    prevLogIdsRef.current = new Set(logs.map((log) => log.id));
  }, [logs]);

  return (
    <div
      data-testid="console-recent-logs"
      onDragEnter={(e) => {
        if (!e.dataTransfer.types.includes(REORDER_MIME) && draggedTranscriptionTimecodes?.length) {
          setIsExternalDragOver(true);
        }
      }}
      onDragLeave={(e) => {
        const nextTarget = e.relatedTarget;
        if (!(nextTarget instanceof Node) || !e.currentTarget.contains(nextTarget)) {
          clearExternalDropState();
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        const isReorder = e.dataTransfer.types.includes(REORDER_MIME);
        e.dataTransfer.dropEffect = isReorder ? "move" : "copy";
        if (!isReorder && draggedTranscriptionTimecodes?.length) {
          setIsExternalDragOver(true);
          setExternalDropIndex(getExternalDropIndex(e.clientY));
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        const reorderedLogId = e.dataTransfer.getData(REORDER_MIME);
        if (reorderedLogId) {
          clearExternalDropState();
          handleReorderDrop(reorderedLogId, dropIndex ?? logs.length);
          clearReorderState();
          return;
        }
        const insertAt = getExternalDropIndex(e.clientY);
        clearExternalDropState();
        let text = e.dataTransfer.getData("text/plain");
        let isOtf = Boolean(e.altKey || e.metaKey);
        let sources: number[] | undefined;
        let startAts: number[] | undefined;
        let endAts: number[] | undefined;
        let words: any[][] | undefined;
        let channels: number[] | undefined;

        const jsonData = e.dataTransfer.getData("application/json");
        if (jsonData) {
          try {
            const parsed = JSON.parse(jsonData);
            if (parsed.text) text = parsed.text;
            if (parsed.isOtf !== undefined) isOtf = isOtf || parsed.isOtf; // Allow alt/meta to override if somehow switch was off but they held key
            if (Array.isArray(parsed.sources)) sources = parsed.sources;
            if (Array.isArray(parsed.startAts)) startAts = parsed.startAts;
            if (Array.isArray(parsed.endAts)) endAts = parsed.endAts;
            if (Array.isArray(parsed.words)) words = parsed.words;
            if (Array.isArray(parsed.channels)) channels = parsed.channels;
          } catch (err) {
            // ignore JSON parse error
          }
        }

        if (text && onDrop) {
          onDrop(text, isOtf, sources, startAts, endAts, words, channels, insertAt);
        }
      }}
      ref={scrollRef}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        overflowY: "auto",
        flex: 1,
        background: "rgba(255,255,255,0.02)",
        borderRadius: "8px",
        padding: "8px",
        border: "1px solid var(--color-panel-border)",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "8px",
          paddingLeft: "8px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: "var(--color-accent-warning)",
              boxShadow: "0 0 8px var(--color-accent-warning)",
            }}
          />
          <span
            style={{
              color: "#fff",
              fontWeight: 900,
              letterSpacing: "1px",
              fontSize: "var(--text-sm)",
              textTransform: "uppercase",
            }}
          >
            {t("logger.console.recentLogs")}{" "}
            <span style={{ fontSize: "var(--text-2xs)", opacity: 0.5 }}>
              {t("logger.console.fastEdit")}
            </span>
          </span>
        </div>

        <button
          type="button"
          data-testid="recent-logs-copy-all"
          onClick={handleCopyAll}
          disabled={logs.length === 0}
          title={copied ? "Copiado" : "Copiar todos los logs"}
          style={{
            background: "transparent",
            border: "1px solid var(--color-panel-border)",
            padding: "4px 8px",
            cursor: logs.length === 0 ? "not-allowed" : "pointer",
            color: copied ? "var(--color-accent-success, #4ade80)" : "rgba(255,255,255,0.6)",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            borderRadius: "4px",
            fontSize: "var(--text-2xs)",
            opacity: logs.length === 0 ? 0.4 : 1,
            transition: "color 0.15s ease, background 0.15s ease",
          }}
          onMouseEnter={(e) => {
            if (logs.length === 0) return;
            e.currentTarget.style.background = "rgba(255,255,255,0.06)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
          }}
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          <span style={{ letterSpacing: "0.5px", textTransform: "uppercase", fontWeight: 700 }}>
            {copied ? "Copiado" : "Copiar"}
          </span>
        </button>
      </div>

      {/* Log entries */}
      {logs.map((log, index) => {
        const isTranscription = (log as any).type === "transcription";
        const isOtf = (log as any).type === "otf";
        const isSyncing = !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          log.id,
        );

        let baseBorderColor = "#f72d60";
        let baseBgColor = "rgba(247, 45, 96, 0.12)";
        let textColor = "rgba(255, 255, 255, 0.8)";
        let tcColor = "rgba(255, 255, 255, 0.8)";
        let opacity = 1;
        let textStyle = "normal";

        if (isOtf) {
          baseBgColor = "rgba(0, 229, 255, 0.12)";
          baseBorderColor = "#00e5ff";
          tcColor = "#00e5ff";
        } else if (isTranscription) {
          baseBgColor = "rgba(156, 39, 176, 0.12)";
          baseBorderColor = "#9c27b0";
          tcColor = "#9c27b0";
          opacity = 0.8;
          textColor = "#ccc";
          textStyle = "italic";
        }

        let finalBorderLeft = `3px solid ${baseBorderColor}`;
        let finalBg = baseBgColor;
        if (editingLogId === log.id) {
          finalBg = "rgba(247,45,96,0.15)";
          finalBorderLeft = "3px solid var(--color-accent-danger)";
        }

        const canReorder = !!onReorderLogs;
        const isDragging = draggingLogId === log.id;
        const showDropBefore = draggingLogId !== null && dropIndex === index;
        const showDropAfter =
          draggingLogId !== null && index === logs.length - 1 && dropIndex === logs.length;
        const isMetadataExpanded = expandedMetadataLogIds.has(log.id);
        const directorName = log.directorId
          ? directors.find((director) => director.id === log.directorId)?.name || log.directorId
          : null;

        const showTranscriptDropBefore =
          isExternalDragOver &&
          draggedTranscriptionTimecodes?.length &&
          externalDropIndex === index;
        const showTranscriptDropAfter =
          isExternalDragOver &&
          draggedTranscriptionTimecodes?.length &&
          index === logs.length - 1 &&
          externalDropIndex === logs.length;

        return (
          <div
            key={log.id}
            ref={(element) => {
              if (element) logEntryRefs.current.set(log.id, element);
              else logEntryRefs.current.delete(log.id);
            }}
            data-log-id={log.id}
            data-testid={`recent-log-entry-${index}`}
            onClick={() => onEditLog?.(log)}
            title={isSyncing ? "Sincronizando… disponible para editar en un instante" : undefined}
            draggable={canReorder}
            onDragStart={
              canReorder
                ? (event) => {
                    event.dataTransfer.setData(REORDER_MIME, log.id);
                    event.dataTransfer.effectAllowed = "move";
                    setDraggingLogId(log.id);
                  }
                : undefined
            }
            onDragEnd={canReorder ? clearReorderState : undefined}
            onDragOver={
              canReorder
                ? (event) => {
                    if (!event.dataTransfer.types.includes(REORDER_MIME)) return;
                    event.preventDefault();
                    event.stopPropagation();
                    event.dataTransfer.dropEffect = "move";
                    const rect = event.currentTarget.getBoundingClientRect();
                    setDropIndex(event.clientY < rect.top + rect.height / 2 ? index : index + 1);
                  }
                : undefined
            }
            style={{
              position: "relative",
              display: "flex",
              gap: "8px",
              alignItems: "flex-start",
              fontSize: "var(--text-base)",
              padding:
                (log.rating || 0) > 0 && editingLogId !== log.id ? "8px 8px 8px 11px" : "8px",
              borderRadius: "4px",
              cursor: isSyncing ? "wait" : "pointer",
              background: finalBg,
              borderLeft: finalBorderLeft,
              opacity: isDragging ? 0.35 : isSyncing ? Math.min(opacity, 0.55) : opacity,
              boxShadow:
                showDropBefore || showTranscriptDropBefore
                  ? "0 -2px 0 0 var(--color-accent-warning)"
                  : showDropAfter || showTranscriptDropAfter
                    ? "0 2px 0 0 var(--color-accent-warning)"
                    : undefined,
            }}
          >
            {showTranscriptDropBefore ? (
              <TranscriptDropIndicator
                index={index}
                label={t("logger.console.dropTranscriptionHere", "Soltar aquí")}
                timecodes={draggedTranscriptionTimecodes}
              />
            ) : null}
            {showTranscriptDropAfter ? (
              <TranscriptDropIndicator
                index={logs.length}
                label={t("logger.console.dropTranscriptionHere", "Soltar aquí")}
                timecodes={draggedTranscriptionTimecodes}
              />
            ) : null}
            {canReorder && (
              <GripVertical
                size={14}
                data-testid={`recent-log-grip-${index}`}
                style={{
                  flexShrink: 0,
                  alignSelf: "center",
                  marginLeft: "-4px",
                  marginRight: "-4px",
                  color: "rgba(255,255,255,0.3)",
                  cursor: "grab",
                }}
              />
            )}
            <span
              style={{
                color: tcColor,
                fontWeight: "bold",
                fontFamily: "var(--font-mono)",
                fontSize: "var(--text-sm)",
              }}
            >
              {stripTimecodeFrames(log.tc)}
            </span>
            <div style={{ flex: 1 }}>
              {log.author && (
                <div
                  data-testid={`recent-log-author-${index}`}
                  style={{
                    color: "rgba(255,255,255,0.5)",
                    fontSize: "var(--text-2xs)",
                    fontWeight: 700,
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    marginBottom: "2px",
                  }}
                >
                  {log.author}
                </div>
              )}
              <div style={{ color: textColor, fontStyle: textStyle as any }}>
                {formatLogText(log.text, participants)}
              </div>
              {(log.tags || []).length > 0 ? (
                <div
                  style={{
                    display: "flex",
                    gap: "4px",
                    flexWrap: "wrap",
                    marginTop: "2px",
                  }}
                >
                  {log.tags?.map((tagId) => {
                    const color = getTagColor(tagId);
                    return (
                      <Badge
                        key={tagId}
                        variant="outline"
                        size="xs"
                        className="border-0 uppercase"
                        style={{
                          background: color,
                          color: isColorLight(color) ? "#000" : "#fff",
                        }}
                      >
                        {getTagLabel(tagId)}
                      </Badge>
                    );
                  })}
                </div>
              ) : null}
              {isMetadataExpanded ? (
                <div
                  style={{
                    display: "flex",
                    gap: "4px",
                    flexWrap: "wrap",
                    marginTop: "4px",
                    paddingTop: "4px",
                    borderTop: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  {log.cameras?.map((cameraId) => {
                    const cameraName = getCameraName(cameraId);
                    return cameraName ? (
                      <Badge
                        key={cameraId}
                        variant="outline"
                        size="xs"
                        className="border-0 bg-[#ff9933] text-black rounded-full"
                      >
                        {cameraName}
                      </Badge>
                    ) : null;
                  })}
                  {directorName ? (
                    <Badge
                      variant="outline"
                      size="xs"
                      className="border-0 bg-[#a855f7] text-white rounded-full uppercase"
                    >
                      {directorName}
                    </Badge>
                  ) : null}
                  {log.participants?.map((participantId) => {
                    const participant = participants.find(
                      (person) => person.id.toLowerCase() === participantId.toLowerCase(),
                    );
                    return participant ? (
                      <ParticipantBadge
                        key={participantId}
                        participant={participant}
                        variant="pill"
                        size="sm"
                      />
                    ) : (
                      <Badge key={participantId} variant="destructive" size="xs">
                        {getParticipantName(participantId)}
                      </Badge>
                    );
                  })}
                </div>
              ) : null}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
              <StarRating
                value={log.rating || 0}
                onChange={(value) => {
                  onUpdateLog?.({ ...log, rating: value > 0 ? value : undefined });
                }}
                inactiveColor="rgba(255,255,255,0.15)"
                size="var(--text-lg)"
              />
              {onDeleteLog && (
                <button
                  type="button"
                  data-testid={`recent-log-delete-${index}`}
                  title={t("logger.console.deleteLog", "Delete log")}
                  onClick={(event) => {
                    event.stopPropagation();
                    onDeleteLog(log.id);
                  }}
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: "4px",
                    cursor: "pointer",
                    color: "rgba(255,255,255,0.4)",
                    display: "flex",
                    alignItems: "center",
                    borderRadius: "4px",
                  }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.color = "var(--color-accent-danger)";
                    event.currentTarget.style.background = "rgba(247,45,96,0.12)";
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.color = "rgba(255,255,255,0.4)";
                    event.currentTarget.style.background = "transparent";
                  }}
                >
                  <Trash2 size={14} />
                </button>
              )}
              <button
                type="button"
                data-testid={`recent-log-metadata-${index}`}
                aria-expanded={isMetadataExpanded}
                title={isMetadataExpanded ? "Ocultar metadatos" : "Ver metadatos"}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleMetadata(log.id);
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: "4px",
                  cursor: "pointer",
                  color: isMetadataExpanded ? "#F72D60" : "#888",
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "4px",
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.color = "#fff";
                  event.currentTarget.style.background = "rgba(255,255,255,0.1)";
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.color = isMetadataExpanded ? "#F72D60" : "#888";
                  event.currentTarget.style.background = "transparent";
                }}
              >
                <i
                  className={`bx ${isMetadataExpanded ? "bx-chevron-up" : "bx-chevron-down"}`}
                  style={{ fontSize: "1.2rem" }}
                ></i>
              </button>
            </div>
          </div>
        );
      })}
      {isExternalDragOver &&
      draggedTranscriptionTimecodes?.length &&
      logs.length === 0 &&
      externalDropIndex === 0 ? (
        <div
          data-testid="transcript-drop-indicator-0"
          role="status"
          aria-label={t("logger.console.dropTranscriptionHere", "Soltar aquí")}
          style={{
            height: "2px",
            flexShrink: 0,
            background: "var(--color-accent-warning)",
          }}
        />
      ) : null}
    </div>
  );
};

export const RecentLogsPanel = React.memo(RecentLogsPanelComponent);
